import * as PIXI from 'pixi.js'

import Settings from '../../settings'
import { measureFont, resolutionsSteps, selectResolutionIndex } from '../utils'

// Texture size less 1 throws WebGL Error
const getCanvasSize = (size, scale) => Math.max(size * scale, 1)

export default class TextDrawer {
  constructor() {
    this.drawedStyle = null
    this.drawedWidth = null
    this.drawedHeight = null
    this.drawedTokens = null

    this.canvases = []
    for (let i = 0; i < resolutionsSteps.length; i++) {
      const canvas = document.createElement('canvas')
      canvas.width = 3
      canvas.height = 3
      const context = canvas.getContext('2d')
      const prevResolutionStep = resolutionsSteps[i - 1] || 1
      const contextScale = (1 / prevResolutionStep) * resolutionsSteps[i]
      const texture = PIXI.Texture.from(canvas, {
        mipmap: PIXI.MIPMAP_MODES.OFF,
        resolution: Settings.TEXT_RESOLUTION,
      })
      const baseTexture = texture.baseTexture
      this.canvases.push({ baseTexture, canvas, context, contextScale, texture })
    }
  }

  draw(style, width, height, tokens, screenResolution) {
    const scaleStep = selectResolutionIndex(screenResolution)
    const alreadyDrawed =
      this.drawedStyle === style &&
      this.drawedWidth === width &&
      this.drawedHeight === height &&
      this.drawedTokens === tokens

    if (alreadyDrawed) {
      return this.canvases[scaleStep].texture
    }

    const { baseTexture, canvas, context } = this.canvases[0]
    const baseScale = resolutionsSteps[0] * Settings.TEXT_RESOLUTION

    canvas.width = getCanvasSize(width, baseScale)
    canvas.height = getCanvasSize(height, baseScale)
    context.scale(baseScale, baseScale)
    this.drawText(context, style, tokens)
    baseTexture.setRealSize(canvas.width, canvas.height, baseScale)

    // Clone canvases from base with downscale
    for (let i = 1; i < this.canvases.length; i++) {
      const { baseTexture, canvas, context, contextScale } = this.canvases[i]
      const { canvas: baseCanvas } = this.canvases[i - 1]
      const scale = resolutionsSteps[i] * Settings.TEXT_RESOLUTION
      canvas.width = getCanvasSize(width, scale)
      canvas.height = getCanvasSize(height, scale)
      context.scale(contextScale, contextScale)
      context.drawImage(baseCanvas, 0, 0)
      baseTexture.setRealSize(canvas.width, canvas.height, scale)
    }

    this.drawedStyle = style
    this.drawedWidth = width
    this.drawedHeight = height
    this.drawedTokens = tokens

    return this.canvases[scaleStep].texture
  }

  drawText(context, style, tokens) {
    context.textBaseline = 'middle'
    context.font = style.font
    context.fillStyle = style.color
    const fontMetrics = measureFont(style.font)
    for (const token of tokens) {
      token.draw(context, fontMetrics)
    }
  }

  destroy() {
    for (let i = 0; i < this.canvases.length; i++) {
      this.canvases[i].texture.destroy(true)

      // make sure to reset the the context and canvas.. dont want this hanging around in memory!
      this.canvases[i].canvas = null
      this.canvases[i].context = null
      this.canvases[i].texture = null
      this.canvases[i].baseTexture = null
      this.canvases[i] = null
    }
  }
}
